import type { Puzzle } from '../../types';

export interface PickPuzzlesOptions {
  pool: Puzzle[];
  /** Si fourni et non vide, ne garde que les puzzles ayant au moins un de ces thèmes. */
  themes?: string[];
  targetRating: number;
  excludeIds?: Set<string>;
  count: number;
  /** Injectable pour des tests déterministes ; `Math.random` par défaut. */
  random?: () => number;
}

/** Fenêtres d'Elo essayées dans l'ordre jusqu'à trouver assez de candidats. */
const RATING_WINDOWS = [100, 200, 400];

/**
 * Choisit jusqu'à `count` puzzles, si possible autour de `targetRating` (la
 * fenêtre s'élargit progressivement s'il n'y a pas assez de candidats
 * proches) et filtrés par thème si demandé. Ordre aléatoire parmi les
 * candidats retenus — pas les `count` premiers du fichier à chaque fois.
 */
export function pickPuzzles(opts: PickPuzzlesOptions): Puzzle[] {
  const random = opts.random ?? Math.random;
  let candidates = opts.pool;
  if (opts.excludeIds && opts.excludeIds.size > 0) {
    candidates = candidates.filter((p) => !opts.excludeIds!.has(p.id));
  }
  if (opts.themes && opts.themes.length > 0) {
    const themeSet = opts.themes;
    candidates = candidates.filter((p) => p.themes.some((t) => themeSet.includes(t)));
  }

  for (const window of RATING_WINDOWS) {
    const inWindow = candidates.filter((p) => Math.abs(p.rating - opts.targetRating) <= window);
    if (inWindow.length >= opts.count) {
      return shuffle(inWindow, random).slice(0, opts.count);
    }
  }
  // Toujours pas assez, même à la fenêtre la plus large : on prend ce qu'il y
  // a, trié par proximité d'Elo plutôt que de renvoyer moins que possible.
  return [...candidates]
    .sort((a, b) => Math.abs(a.rating - opts.targetRating) - Math.abs(b.rating - opts.targetRating))
    .slice(0, opts.count);
}

function shuffle<T>(arr: T[], random: () => number): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}
